import React, { createContext, useMemo } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';

// Define the custom palette
const getAppTheme = (mode) => ({
  palette: {
    mode,
    // --- Custom Colors based on your Vision ---
    primary: {
      main: '#4A90E2', // A supportive, trustworthy blue (Mission focus)
      light: '#6FA8EE',
      dark: '#356BB0',
    },
    secondary: {
      main: '#50E3C2', // An energetic, positive teal (Support/Connection focus)
      light: '#72E9D4',
      dark: '#38A087',
    },
    background: {
      default: mode === 'light' ? '#F4F7F9' : '#121212', // Light, clean background
      paper: mode === 'light' ? '#FFFFFF' : '#1D1D1D',
    },
    text: {
      primary: mode === 'light' ? '#333333' : '#E0E0E0',
      secondary: mode === 'light' ? '#666666' : '#AAAAAA',
    },
    // --- Custom Components & Typography ---
  },
  typography: {
    fontFamily: ['"Poppins"', 'sans-serif'].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 600,
    },
    button: {
      textTransform: 'none', // Community-focused, less formal
      fontWeight: 600,
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Slightly rounded for a friendly feel
          padding: '10px 24px',
        },
      },
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                borderRadius: 12, // More rounded corners for cards/modals
            }
        }
    }
  }
});

// The actual Theme Context is mostly for toggling dark/light mode, which we'll add later
export const ColorModeContext = createContext({ toggleColorMode: () => {} });

export function ThemeContextProvider({ children }) {
  // Simple state for demonstration, you'd integrate this with user preferences
  const [mode, setMode] = React.useState('light'); 

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  // Memoize the theme object to prevent unnecessary re-renders
  const theme = useMemo(() => createTheme(getAppTheme(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        {/* CssBaseline resets styles and applies background/text colors */}
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// Don't forget to include the Poppins font in your public/index.html or global CSS!